import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const FIELD_LABELS = {
  material: "Materiale", thickness_mm: "Spessore (mm)", quantity: "Quantità",
  finish: "Finitura", overall_dimensions_mm: "Ingombro componente (mm)", diameter_mm: "Diametro finito (mm)",
};
const FEATURE_LABELS = {
  hole: "Fori", slot: "Asole", bend: "Pieghe", rolling: "Calandrature", weld: "Saldature", assembly: "Assemblaggi",
};
const STATE_HELP = {
  EXPLICIT_ON_DRAWING: "esplicito sul disegno (richiede valore + evidenza/zona)",
  NOT_SHOWN: "non indicato sul disegno",
  UNREADABLE: "presente ma illeggibile",
  NOT_APPLICABLE: "non applicabile a questo disegno",
  EXTERNAL_JOB_DATA: "lo so dalla commessa, NON dal disegno (non giudica la lettura)",
};

const ItemRow = ({ label, item, states, isFeature, onChange, testid }) => {
  const set = (k, v) => onChange({ ...item, [k]: v });
  return (
    <div className="border border-border p-2 space-y-1" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap">
        <b className="text-[12px] w-44">{label}</b>
        <select className="border border-border text-[11px] h-6 px-1" value={item.state || ""} onChange={(e) => set("state", e.target.value || null)} data-testid={`${testid}-state`}>
          <option value="">— stato —</option>
          {states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {isFeature ? (
          <Input className="h-6 text-[11px] w-20" type="number" placeholder="n." value={item.count ?? ""} onChange={(e) => set("count", e.target.value === "" ? null : Number(e.target.value))} data-testid={`${testid}-count`} />
        ) : (
          <Input className="h-6 text-[11px] w-40" placeholder="valore" value={item.value ?? ""} onChange={(e) => set("value", e.target.value === "" ? null : e.target.value)} data-testid={`${testid}-value`} />
        )}
        <Input className="h-6 text-[11px] w-16" placeholder="unità" value={item.unit ?? ""} onChange={(e) => set("unit", e.target.value || null)} />
        <Input className="h-6 text-[11px] w-14" type="number" placeholder="pag." value={item.page ?? ""} onChange={(e) => set("page", e.target.value === "" ? null : Number(e.target.value))} />
        <Input className="h-6 text-[11px] w-24" placeholder="vista/zona" value={item.zone ?? ""} onChange={(e) => set("zone", e.target.value || null)} />
        <Input className="h-6 text-[11px] w-20" type="number" step="0.01" placeholder="tolleranza" value={item.tolerance ?? ""} onChange={(e) => set("tolerance", e.target.value === "" ? null : Number(e.target.value))} />
      </div>
      <div className="flex gap-1">
        <Input className="h-6 text-[11px] flex-1" placeholder="evidenza testuale/geometrica letta sul disegno" value={item.evidence_text ?? ""} onChange={(e) => set("evidence_text", e.target.value || null)} data-testid={`${testid}-evidence`} />
        <Input className="h-6 text-[11px] flex-1" placeholder="nota del tecnico" value={item.note ?? ""} onChange={(e) => set("note", e.target.value || null)} />
      </div>
      {item.state && <p className="text-[10px] text-muted-foreground">{STATE_HELP[item.state]}</p>}
    </div>
  );
};

export const P05GroundTruthForm = ({ neutralId, pages, onLocked }) => {
  const [gt, setGt] = useState(null);
  const [states, setStates] = useState([]);
  const [opCats, setOpCats] = useState([]);
  const [locked, setLocked] = useState(false);
  const [hidden, setHidden] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    Promise.all([
      api.get(`/p05/gt-template`),
      api.get(`/p05/cases/${neutralId}/ground-truth`),
    ]).then(([t, g]) => {
      setStates(t.data.states); setOpCats(t.data.operation_categories);
      if (g.data.locked && !g.data.payload) { setLocked(true); setHidden(g.data.hidden_reason); return; }
      setLocked(Boolean(g.data.locked));
      setGt(g.data.payload || t.data.template);
    }).catch(() => toast.error("Errore caricamento FASE A"));
  }, [neutralId]);

  const imgUrl = useMemo(() => `${api.defaults.baseURL}/p05/cases/${neutralId}/page-image/${page}`, [neutralId, page]);

  if (hidden) return <p className="text-xs text-amber-800" data-testid="p05-gt-hidden">FASE A bloccata e nascosta: {hidden}</p>;
  if (!gt) return <p className="text-xs">Caricamento…</p>;

  const save = async () => {
    setBusy(true);
    try { await api.put(`/p05/cases/${neutralId}/ground-truth`, { payload: gt }); toast.success("Bozza salvata"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const lock = async () => {
    setBusy(true);
    try {
      await api.put(`/p05/cases/${neutralId}/ground-truth`, { payload: gt });
      const r = await api.post(`/p05/cases/${neutralId}/ground-truth/lock`);
      toast.success(`FASE A bloccata (sha ${r.data.payload_sha256.slice(0, 8)}…)`);
      setLocked(true); onLocked && onLocked();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore lock"); }
    setBusy(false);
  };
  const setField = (sec, k, v) => setGt({ ...gt, [sec]: { ...gt[sec], [k]: v } });
  const addOp = () => setGt({ ...gt, operations: [...(gt.operations || []), { name: "", category: "explicit_operation", state: "EXPLICIT_ON_DRAWING", evidence_text: "", note: "" }] });
  const setOp = (i, k, v) => { const ops = [...gt.operations]; ops[i] = { ...ops[i], [k]: v }; setGt({ ...gt, operations: ops }); };

  return (
    <div className="grid lg:grid-cols-2 gap-3" data-testid="p05-gt-form">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setPage(Math.max(1, page - 1))}>◀</Button>
          <span className="text-[11px]">pagina {page} / {pages}</span>
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setPage(Math.min(pages, page + 1))}>▶</Button>
        </div>
        <img src={imgUrl} alt={`pagina ${page}`} className="border border-border w-full" data-testid="p05-gt-page-image" />
        <p className="text-[10px] text-amber-800">Compila guardando SOLO il disegno: nessun output QuoteOS è mostrato prima del blocco.</p>
      </div>
      <div className="space-y-2">
        <Input className="h-6 text-[11px]" placeholder="tipologia generale del disegno (es. lamiera piegata, calandrato...)" value={gt.general_type ?? ""} onChange={(e) => setGt({ ...gt, general_type: e.target.value || null })} data-testid="p05-gt-type" />
        <h4 className="text-xs font-semibold">Campi</h4>
        {Object.keys(FIELD_LABELS).map((k) => (
          <ItemRow key={k} label={FIELD_LABELS[k]} item={gt.fields[k]} states={states} isFeature={false} onChange={(v) => setField("fields", k, v)} testid={`p05-gt-field-${k}`} />
        ))}
        <h4 className="text-xs font-semibold">Feature fisiche visibili (conteggi)</h4>
        {Object.keys(FEATURE_LABELS).map((k) => (
          <ItemRow key={k} label={FEATURE_LABELS[k]} item={gt.features[k]} states={states} isFeature onChange={(v) => setField("features", k, v)} testid={`p05-gt-feature-${k}`} />
        ))}
        <h4 className="text-xs font-semibold">Quote leggibili (totale)</h4>
        <ItemRow label="Numero quote" item={gt.dimensions_total} states={states} isFeature onChange={(v) => setGt({ ...gt, dimensions_total: v })} testid="p05-gt-dimensions" />
        <h4 className="text-xs font-semibold">Lavorazioni</h4>
        {(gt.operations || []).map((op, i) => (
          <div key={i} className="flex gap-1 items-center" data-testid={`p05-gt-op-${i}`}>
            <Input className="h-6 text-[11px] w-40" placeholder="nome lavorazione" value={op.name} onChange={(e) => setOp(i, "name", e.target.value)} />
            <select className="border border-border text-[11px] h-6" value={op.category} onChange={(e) => setOp(i, "category", e.target.value)}>
              {opCats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="border border-border text-[11px] h-6" value={op.state} onChange={(e) => setOp(i, "state", e.target.value)}>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Input className="h-6 text-[11px] flex-1" placeholder="evidenza" value={op.evidence_text ?? ""} onChange={(e) => setOp(i, "evidence_text", e.target.value)} />
          </div>
        ))}
        <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={addOp} data-testid="p05-gt-add-op">+ lavorazione</Button>
        <Textarea className="text-[11px]" placeholder="note del tecnico" value={gt.technician_notes ?? ""} onChange={(e) => setGt({ ...gt, technician_notes: e.target.value || null })} />
        {!locked && (
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={save} data-testid="p05-gt-save">Salva bozza</Button>
            <Button size="sm" variant="destructive" className="h-7 text-xs" disabled={busy} onClick={lock} data-testid="p05-gt-lock">Blocca FASE A (irreversibile)</Button>
          </div>
        )}
        {locked && <Badge className="bg-emerald-100 text-emerald-800">FASE A bloccata</Badge>}
      </div>
    </div>
  );
};
